import React, { useState, useEffect } from 'react';
import { Tabs, Button, Dropdown, message } from 'antd';
import {
  FileTextOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SendOutlined,
  CalendarOutlined,
  FileSearchOutlined,
  AuditOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Layout from '../../components/Layout';
import PageHeadingBanner from '../../components/shared/PageHeadingBanner';
import DashboardStatCard from '../../components/shared/DashboardStatCard';
import ReportsTab from './ReportsTab';
import ApprovalQueueTab from './ApprovalQueueTab';
import SchedulesTab from './SchedulesTab';
import TemplatesTab from './TemplatesTab';
import AuditTrailTab from './AuditTrailTab';
import GenerateReportModal from './GenerateReportModal';
import { getRegulatoryReportStats, exportRegulatoryReports } from '../../services/regulatoryReportingService';
import './RegulatoryReporting.css';

const RegulatoryReporting = () => {
  const [activeTab, setActiveTab] = useState('reports');
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pendingApproval: 0,
    approved: 0,
    submitted: 0,
    scheduled: 0,
  });

  const fetchStats = async () => {
    try {
      const res = await getRegulatoryReportStats();
      if (res?.status) {
        setStats(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // ── Export Handlers ──────────────────────────────────────────────────────
  const getExportData = async () => {
    setExporting(true);
    try {
      const res = await exportRegulatoryReports({});
      if (!res?.status || !res.data?.length) {
        message.warning('No records to export');
        return null;
      }
      return res.data.map((report) => ({
        'Report Name': report.reportName || '',
        'Report Type': report.reportType || '',
        'Reporting Period Start': report.startDate || '',
        'Reporting Period End': report.endDate || '',
        'Status': report.status || '',
        'Generated By': report.generatedBy || '',
        'Generation Date': report.generatedAt || '',
        'Approved By': report.approvedBy || '',
        'Approval Date': report.approvedAt || '',
        'Submitted By': report.submittedBy || '',
        'Submission Date': report.submittedAt || '',
      }));
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async () => {
    const data = await getExportData();
    if (!data) return;

    const ws = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `regulatory_reports_${new Date().toISOString().split('T')[0]}.csv`);
    message.success('CSV exported successfully');
  };

  const handleExportExcel = async () => {
    const data = await getExportData();
    if (!data) return;

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Regulatory Reports');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `regulatory_reports_${new Date().toISOString().split('T')[0]}.xlsx`);
    message.success('Excel file exported successfully');
  };

  const exportMenuItems = [
    {
      key: 'csv',
      label: 'Export as CSV',
      icon: <DownloadOutlined />,
      onClick: handleExportCSV,
    },
    {
      key: 'excel',
      label: 'Export as Excel',
      icon: <FileExcelOutlined />,
      onClick: handleExportExcel,
    },
  ];

  // ── Tabs Configuration ──────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'reports',
      label: (
        <span className="reg-tab-badge">
          <FileTextOutlined className="tab-icon" />
          All Reports
        </span>
      ),
      children: <ReportsTab />,
    },
    {
      key: 'approval',
      label: (
        <span className="reg-tab-badge">
          <CheckCircleOutlined className="tab-icon" />
          Approval Queue
        </span>
      ),
      children: <ApprovalQueueTab />,
    },
    {
      key: 'schedules',
      label: (
        <span className="reg-tab-badge">
          <CalendarOutlined className="tab-icon" />
          Schedules
        </span>
      ),
      children: <SchedulesTab />,
    },
    {
      key: 'templates',
      label: (
        <span className="reg-tab-badge">
          <FileSearchOutlined className="tab-icon" />
          Templates
        </span>
      ),
      children: <TemplatesTab />,
    },
    {
      key: 'audit',
      label: (
        <span className="reg-tab-badge">
          <AuditOutlined className="tab-icon" />
          Audit Trail
        </span>
      ),
      children: <AuditTrailTab />,
    },
  ];

  return (
    <Layout>
      <div className="reg-wrapper" style={{ padding: '0 24px' }}>
        {/* ── Hero Header ───────────────────────────────────────────── */}
        <PageHeadingBanner
          icon={<FileTextOutlined />}
          title="Regulatory Reporting"
          subtitle="Generate, review, approve, and submit regulatory reports to meet compliance obligations"
          actions={
            <>
              <Button
                icon={<PlusOutlined />}
                className="phb-btn-solid"
                size="large"
                onClick={() => setGenerateModalVisible(true)}
              >
                Generate Report
              </Button>
              <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                <Button
                  icon={<DownloadOutlined />}
                  className="phb-btn-solid"
                  size="large"
                  loading={exporting}
                  style={{ background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.3)' }}
                >
                  Export
                </Button>
              </Dropdown>
            </>
          }
        />

        {/* ── Dashboard Stats ───────────────────────────────────────── */}
        <div className="reg-dashboard-section">
          <div className="ds-stats-grid cols-5">
            <DashboardStatCard
              icon={<FileTextOutlined />}
              value={stats.total || 0}
              label="Total Reports"
              iconBg="#fff3eb"
              iconColor="#ff6700"
            />
            <DashboardStatCard
              icon={<ClockCircleOutlined />}
              value={stats.pendingApproval || 0}
              label="Pending Approval"
              iconBg="#fffbe6"
              iconColor="#faad14"
            />
            <DashboardStatCard
              icon={<CheckCircleOutlined />}
              value={stats.approved || 0}
              label="Approved"
              iconBg="#f6ffed"
              iconColor="#52c41a"
            />
            <DashboardStatCard
              icon={<SendOutlined />}
              value={stats.submitted || 0}
              label="Submitted"
              iconBg="#e6f7ff"
              iconColor="#1890ff"
            />
            <DashboardStatCard
              icon={<CalendarOutlined />}
              value={stats.scheduled || 0}
              label="Scheduled"
              iconBg="#e6fffb"
              iconColor="#13c2c2"
            />
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────── */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="reg-tabs"
        />

        {/* ── Generate Report Modal ─────────────────────────────────── */}
        <GenerateReportModal
          visible={generateModalVisible}
          onClose={() => setGenerateModalVisible(false)}
          onSuccess={() => {
            fetchStats();
            // The ReportsTab will auto-refresh via its own fetchReports
          }}
        />
      </div>
    </Layout>
  );
};

export default RegulatoryReporting;
